import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Set up axios defaults
axios.defaults.baseURL = API_URL;

// Add token and school code to requests if available
const token = localStorage.getItem('token');
const schoolCode = localStorage.getItem('schoolCode');

if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

if (schoolCode) {
  axios.defaults.headers.common['X-School-Code'] = schoolCode;
}

// Axios interceptor to always include school code
axios.interceptors.request.use((config) => {
  const currentSchoolCode = localStorage.getItem('schoolCode');
  if (currentSchoolCode) {
    config.headers['X-School-Code'] = currentSchoolCode;
  }
  return config;
});

// Auth services
export const authService = {
  login: (email: string, password: string, schoolCode: string) => 
    axios.post('/auth/login', { email, password, schoolCode }),
  
  register: (data: any) => 
    axios.post('/auth/register', data),
  
  getCurrentUser: () => 
    axios.get('/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) => 
    axios.put('/auth/change-password', { currentPassword, newPassword }),
  
  verifySchool: (schoolCode: string) =>
    axios.get(`/tenants/verify/${schoolCode}`)
};

// Student services
export const studentService = {
  getAll: (params?: any) => 
    axios.get('/students', { params }),
  
  getById: (id: string) => 
    axios.get(`/students/${id}`),
  
  create: (data: any) => 
    axios.post('/students', data),
  
  update: (id: string, data: any) => 
    axios.put(`/students/${id}`, data),
  
  delete: (id: string) => 
    axios.delete(`/students/${id}`),
};

// Teacher services
export const teacherService = {
  getAll: () => 
    axios.get('/teachers'),
  
  getById: (id: string) => 
    axios.get(`/teachers/${id}`),
  
  create: (data: any) => 
    axios.post('/teachers', data),
  
  update: (id: string, data: any) => 
    axios.put(`/teachers/${id}`, data),
  
  delete: (id: string) => 
    axios.delete(`/teachers/${id}`),
  
  getProfile: () =>
    axios.get('/teachers/profile'),
  
  updateProfile: (data: any) =>
    axios.put('/teachers/profile', data),
  
  uploadProfilePicture: (formData: FormData) =>
    axios.post('/teachers/profile/picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
};

// Attendance services
export const attendanceService = {
  markAttendance: (data: any) => 
    axios.post('/attendance', data),
  
  getStudentAttendance: (studentId: string, params?: any) => 
    axios.get(`/attendance/student/${studentId}`, { params }),
  
  getClassAttendance: (classId: string, params?: any) => 
    axios.get(`/attendance/class/${classId}`, { params }),
  
  getAttendanceReport: (classId: string, params?: any) => 
    axios.get(`/attendance/report/${classId}`, { params }),
};

// Result services
export const resultService = {
  getAll: (params?: any) => 
    axios.get('/results', { params }),
  
  create: (data: any) => 
    axios.post('/results', data),
  
  uploadResult: (data: any) => 
    axios.post('/results', data),
  
  getStudentResults: (studentId: string, params?: any) => 
    axios.get(`/results/student/${studentId}`, { params }),
  
  getClassResults: (classId: string, params?: any) => 
    axios.get(`/results/class/${classId}`, { params }),
  
  updateResult: (id: string, data: any) => 
    axios.put(`/results/${id}`, data),
  
  delete: (id: string) => 
    axios.delete(`/results/${id}`),
  
  deleteResult: (id: string) => 
    axios.delete(`/results/${id}`),
};

// Notice services
export const noticeService = {
  getAll: (params?: any) => 
    axios.get('/notices', { params }),
  
  getById: (id: string) => 
    axios.get(`/notices/${id}`),
  
  create: (data: any) => 
    axios.post('/notices', data),
  
  update: (id: string, data: any) => 
    axios.put(`/notices/${id}`, data),
  
  delete: (id: string) => 
    axios.delete(`/notices/${id}`),
};

// Fee services
export const feeService = {
  getStudentFees: (studentId: string, params?: any) => 
    axios.get(`/fees/student/${studentId}`, { params }),
  
  getPendingFees: () => 
    axios.get('/fees/pending'),
  
  createFee: (data: any) => 
    axios.post('/fees', data),
  
  recordPayment: (data: any) => 
    axios.post('/fees/payment', data),
  
  getFeeSummary: (studentId: string) => 
    axios.get(`/fees/summary/${studentId}`),
  
  updateFee: (id: string, data: any) => 
    axios.put(`/fees/${id}`, data),
};

// Class services
export const classService = {
  getAll: () => 
    axios.get('/classes'),
  
  create: (data: any) => 
    axios.post('/classes', data),
  
  update: (id: string, data: any) => 
    axios.put(`/classes/${id}`, data),
  
  delete: (id: string) => 
    axios.delete(`/classes/${id}`),
};

// Subject services
export const subjectService = {
  getAll: () => 
    axios.get('/subjects'),
  
  create: (data: any) => 
    axios.post('/subjects', data),
  
  update: (id: string, data: any) => 
    axios.put(`/subjects/${id}`, data),
  
  delete: (id: string) => 
    axios.delete(`/subjects/${id}`),
};

// Timetable services
export const timetableService = {
  getClassTimetable: (classId: string) => 
    axios.get(`/timetable/class/${classId}`),
  
  getTeacherTimetable: () => 
    axios.get('/timetable/teacher'),
  
  create: (data: any) => 
    axios.post('/timetable', data),
};

// Assignment services
export const assignmentService = {
  getAll: (params?: any) => 
    axios.get('/assignments', { params }),
  
  getClassAssignments: (classId: string) => 
    axios.get(`/assignments/class/${classId}`),
  
  create: (data: any) => 
    axios.post('/assignments', data),
  
  update: (id: string, data: any) => 
    axios.put(`/assignments/${id}`, data),
  
  delete: (id: string) => 
    axios.delete(`/assignments/${id}`),
  
  submitAssignment: (id: string, data: any) => 
    axios.post(`/assignments/${id}/submit`, data),
};

// Dashboard services
export const dashboardService = {
  getStats: () => 
    axios.get('/dashboard/stats'),
  
  getRecentActivity: () => 
    axios.get('/dashboard/recent-activity'),
};

export default axios;
